import { LitElement } from '@lit'
import { viewportObserver } from '@nova/utils'

class NovaProductGallery extends LitElement {
  static properties = {
    viewport: { type: String, state: true },
    galleryCarousel: { type: Object, state: true },
    thumbnailCarousel: { type: Object, state: true },
    thumbnailPrevious: { type: Object, state: true },
    thumbnailNext: { type: Object, state: true },
    zoomInOverlay: { type: Object, state: true },
    zoomButtons: { type: Object, state: true },
    activeGalleryItem: { type: Object, state: true },
    displayNavButtons: { type: Boolean },
    zoomActive: { type: Boolean },
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.galleryCarousel = null
    this.thumbnailCarousel = null
    this.thumbnailPrevious = null
    this.thumbnailNext = null
    this.zoomInOverlay = null
    this.zoomButtons = null
    this.activeGalleryItem = null
    this.observer = null
    this.displayNavButtons = true
    this.zoomActive = false
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    viewportObserver.removeListener(this._handleViewportChange)
    document.removeEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  firstUpdated() {
    this.thumbnailCarousel = this.querySelector('.js-thumbnails-carousel')
    this.galleryCarousel = this.querySelector('.js-gallery-carousel')
    this.thumbnailPrevious = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--previous',
    )
    this.thumbnailNext = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--next',
    )
    this.zoomButtons = this.querySelectorAll('.js-zoom-button')

    for (const button of this.zoomButtons) {
      button.addEventListener('click', () => {
        this.handleZoomEvents(button, 3)
      })
    }

    this.checkDisplayNavButtons()
    this.hideDisabledButton(this.thumbnailPrevious)
    this.hideDisabledButton(this.thumbnailNext)
    this.handleThumbnailCarouselAttribute()
    this.handleThumbnailCarouselClick()
    this.handleGalleryCarouselMove()
    this.resetCarousel()
    this.hideDisabledButton(this.thumbnailPrevious)
    this.hideDisabledButton(this.thumbnailNext)
    this.initMutationObserver()
  }

  checkDisplayNavButtons() {
    const carouselItems =
      this.thumbnailCarousel.querySelectorAll('sl-carousel-item')
    const slidePerPage = Number(
      this.thumbnailCarousel.getAttribute('slides-per-page'),
    )
    if (carouselItems.length <= slidePerPage) this.displayNavButtons = false
  }

  initMutationObserver() {
    if (!this.displayNavButtons) return
    if (this.observer) {
      this.observer.disconnect()
    }
    const handleClassChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          this.hideDisabledButton(this.thumbnailPrevious)
          this.hideDisabledButton(this.thumbnailNext)
        }
      }
    }
    this.observer = new MutationObserver(handleClassChange)
    if (this.thumbnailPrevious) {
      this.observer.observe(this.thumbnailPrevious, { attributes: true })
    }
    if (this.thumbnailNext) {
      this.observer.observe(this.thumbnailNext, { attributes: true })
    }
  }

  hideDisabledButton(button) {
    if (
      button.classList.contains('carousel__navigation-button--disabled') ||
      !this.displayNavButtons
    ) {
      button.style.opacity = 0
    } else {
      button.style.opacity = 1
    }
  }

  resetCarousel() {
    const carousels = this.querySelectorAll('sl-carousel')
    for (const carousel of carousels) {
      carousel.goToSlide(0)
    }
  }

  updateGalleryContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }

    requestAnimationFrame(() => {
      this.firstUpdated()
    })
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
    this.handleThumbnailCarouselAttribute()
  }

  handleThumbnailCarouselAttribute() {
    if (!this.thumbnailCarousel) return

    const slidesPerPage =
      this.viewport === 'desktop' || this.viewport === 'widescreen' ? '6' : '5'
    this.thumbnailCarousel.setAttribute('slides-per-page', slidesPerPage)
  }

  handleThumbnailCarouselClick() {
    if (!this.thumbnailCarousel) return

    const handleEvent = (event) => {
      if (event.type === 'click' || event.key === 'Enter') {
        const card = event.target.closest('.thumbnails-item')
        if (card) {
          this.handleGalleryCarouselDisplay(card)
        }
      }
    }

    this.thumbnailCarousel.addEventListener('click', handleEvent)
    this.thumbnailCarousel.addEventListener('keydown', handleEvent)
  }

  handleGalleryCarouselDisplay(card) {
    if (!this.galleryCarousel || card.classList.contains('active')) return

    const activeCard = this.thumbnailCarousel.querySelector('.active')
    if (activeCard) activeCard.classList.remove('active')

    card.classList.add('active')
    const cardPosition = card.dataset.position
    if (cardPosition) {
      const displayCardIndex = Number(cardPosition) - 1
      this.galleryCarousel.goToSlide(displayCardIndex)
    }
  }

  handleGalleryCarouselMove() {
    if (!this.galleryCarousel || !this.thumbnailCarousel) return

    this.galleryCarousel.addEventListener('sl-slide-change', (event) => {
      const activeGalleryIndex = event.detail.index
      this.handleVideoAutoPlay(event.detail.slide)
      this.thumbnailCarousel.goToSlide(Number(activeGalleryIndex))
      const oldActiveThumbnail = this.thumbnailCarousel.querySelector('.active')
      if (oldActiveThumbnail) oldActiveThumbnail.classList.remove('active')
      const newActiveThumbnail = this.thumbnailCarousel.querySelector(
        `[data-position="${activeGalleryIndex + 1}"]`,
      )
      if (newActiveThumbnail) newActiveThumbnail.classList.add('active')
    })
  }

  handleVideoAutoPlay(slide) {
    const media = slide.querySelector('nova-adaptive-video')
    if (!media) return
    media.play()
  }

  zoomMoveWithHover(image, event, zoomRatio) {
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    this.zoomInOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.zoomInOverlay.style.backgroundSize = `${image.width * zoomRatio}px`
  }

  handleZoomEvents(button, zoomRatio) {
    this.activeGalleryItem = button.closest('.gallery-item')
    this.zoomInOverlay = this.activeGalleryItem.querySelector('.zoom-overlay')

    if (!this.zoomInOverlay) return

    if (this.zoomActive) {
      this.handleOverlayRemove(button)
      this.zoomActive = false
    } else {
      this.zoomInOverlay.classList.remove('hidden')
      this.activeGalleryItem.classList.add('zoomed')
      const image = this.activeGalleryItem.querySelector('.js-zoom-image')

      this.zoomInOverlay.addEventListener('mousemove', (event) =>
        this.zoomMoveWithHover(image, event, zoomRatio),
      )

      this.activeGalleryItem.addEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, button),
      )

      this.zoomActive = true

      button.classList.remove('zoom-in')
      button.classList.add('zoom-out')
      button.setAttribute('aria-label', window.productStrings.zoomOut)
    }
  }

  handleOverlayRemove(button) {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')
      this.zoomInOverlay.removeEventListener(
        'mousemove',
        this.zoomMoveWithHover,
      )
      this.zoomInOverlay = null
    }

    if (this.activeGalleryItem) {
      this.activeGalleryItem.removeEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, button),
      )
      this.activeGalleryItem.classList.remove('zoomed')
      this.activeGalleryItem = null
    }

    this.zoomActive = false

    button.classList.remove('zoom-out')
    button.classList.add('zoom-in')
    button.setAttribute('aria-label', window.productStrings.zoomIn)
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-product-gallery', NovaProductGallery)
